import { type CartState } from "../types";
import * as PriceUtils from "../../../../../utils/PriceUtils";
import { createSelector } from "reselect";
import { isPlainLineItem, type LineItem, type PlainLineItem, type SubLineItem } from "../../models/CartData";
import * as WeightUtils from "../../../../../utils/WeightUtils";
import type { RootState } from "~/app/store";
import { CART_PNA_THRESHOLD } from "../../../../../utils/constants";
import { isKITKITProduct } from "~/domains/product/types/ProductData";
import { getTotalPriceForLineItem } from "~/domains/product/utils/productDescriptorUtils";

type PageWithCartState = { cart: CartState };

/**
 * Calculates the total item count across all line items in the cart.
 * Includes quantities from optional parts (for KIT products).
 */
export const selectTotalItemCount = createSelector(
  (state: PageWithCartState) => state.cart.packEntriesById,
  (packEntriesById) => {
    return Object.values(packEntriesById).reduce((total, lineItem) => {
      return total + lineItem.quantity;
    }, 0);
  },
);

export const selectTotalCartWeight = createSelector(
  (state: PageWithCartState) => state.cart.packEntriesById,
  (packEntriesById) => {
    const weights = Object.values(packEntriesById).map((entry: LineItem) =>
      WeightUtils.multiply(entry.weight, entry.quantity),
    );
    return WeightUtils.sum(...weights);
  },
);

export const selectTotalCartPrice = (state: RootState) => {
  const packEntries: LineItem[] = Object.values(state.cart.packEntriesById);
  const totalPerPack = packEntries
    .filter((lineItem) => !isKITKITProduct(state.products.basic[lineItem.productCode]))
    .map((packEntry) => {
      if (packEntry.netPrice) {
        const product = state.products.basic[packEntry.productCode];
        return getTotalPriceForLineItem(packEntry, product);
      }
      return null;
    });
  let totalPrice;
  if (totalPerPack.every((value) => !!value)) {
    // @ts-ignore
    totalPrice = PriceUtils.volatileSum(...totalPerPack);
  } else {
    totalPrice = null;
  }
  return totalPrice;
};

export const selectTotalCartPriceWithoutDiscount = (state: RootState): PriceData | null => {
  if (!window.SCHA.feature.campaigndiscount) {
    return null;
  }
  const packEntries: LineItem[] = Object.values(state.cart.packEntriesById);
  const totalPerPack = packEntries
    .filter((lineItem) => !isKITKITProduct(state.products.basic[lineItem.productCode]))
    .map((packEntry) => {
      if (packEntry.netPrice) {
        if (packEntry.saleCampaignEnabled) {
          return {
            quantity: packEntry.quantity,
            actualNetPrice: packEntry.actualNetPrice,
            actualSalePrice: packEntry.actualSalePrice,
            currencyIso: packEntry.actualNetPrice?.currencyIso,
          };
        }
      }
      return null;
    })
    .filter((item) => item !== null) as {
    quantity: number;
    actualNetPrice: { value: number; currencyIso: string };
    actualSalePrice: { value: number };
    currencyIso: string;
  }[];

  if (totalPerPack.length === 0) {
    return null;
  }

  const totalDiscount = totalPerPack.reduce((total, product) => {
    if (product.actualNetPrice && product.actualSalePrice) {
      const discountPerUnit = product.actualNetPrice.value - product.actualSalePrice.value;
      return total + discountPerUnit * product.quantity;
    }
    return total;
  }, 0);

  // Assuming all products have the same currency, take the first one
  const currencyIso = totalPerPack[0].currencyIso;

  return {
    value: totalDiscount,
    currencyIso,
  };
};

export const selectIsCartAboveThreshold = (state: RootState) => state.cart.cartEntries.length > CART_PNA_THRESHOLD;

export const selectShowOptimePopupOnCheckout = (state: RootState) => {
  const allProducts = state.products.basic;
  return Object.values(state.cart.cartEntriesById).some((entry) => allProducts[entry.productCode].isOptime);
};

export const selectPackageEntry = (packEntryId: number) => (state: RootState) =>
  state.cart.packEntriesById[packEntryId];
export const selectPackageEntryMap = (state: RootState) => state.cart.packEntriesById;
export const selectCartEntryMap = (state: RootState) => state.cart.cartEntriesById;
export const selectCartEntries = (state: RootState) => state.cart.cartEntries;
export const selectCartEntry = (cartEntryId: number) => (state: RootState) => state.cart.cartEntriesById[cartEntryId];
export const selectLineItem = (packEntryId: number) => (state: RootState) => state.cart.packEntriesById[packEntryId];

export const selectProductCodeFromPackage = (packEntryId: number) => (state: RootState) => {
  return selectPackageEntry(packEntryId)(state).productCode;
};

export const selectSubLineItems = (packEntryId: number) => (state: RootState) => {
  const packEntry = selectPackageEntry(packEntryId)(state) as PlainLineItem;
  return (packEntry.optionalPartIds ?? []).map((id) => selectPackageEntry(id)(state)) as SubLineItem[];
};

/**
 * Returns the package entries grouped by product code.
 * Package entries will be ordered by their entryNumber.
 */
export const selectLineItemsByProduct = createSelector(
  (state: RootState) => state.cart.packEntriesById,
  (packEntriesById) => {
    const entriesByProduct: Record<string, LineItem[]> = {};
    Object.values(packEntriesById).forEach((packEntry) => {
      const productCode = packEntry.productCode;
      if (!entriesByProduct[productCode]) {
        entriesByProduct[productCode] = [];
      }
      entriesByProduct[productCode].push(packEntry);
    });
    Object.values(entriesByProduct).forEach((entries) => {
      entries.sort((a, b) => (a.entryNumber ?? -1) - (b.entryNumber ?? -1));
    });
    return entriesByProduct;
  },
);
